/** Keep the server-rendered login hints current without requiring a page reload. */
export function initializeServiceStatus(container: HTMLElement) {
  let pending = false;
  let stopped = false;
  let expiry: ReturnType<typeof setTimeout> | undefined;

  function expire() {
    if (undefined !== expiry) clearTimeout(expiry);
    const now = Date.now();
    const ends: number[] = [];
    container.querySelectorAll<HTMLDetailsElement>("details[data-announcement]").forEach((details) => {
      const end = Date.parse(details.dataset.endsAt ?? "");
      if (end <= now) details.remove();
      else if (Number.isFinite(end)) ends.push(end);
    });
    if (!container.querySelector("details[data-announcement]")) container.replaceChildren();
    if (ends.length > 0) expiry = setTimeout(expire, Math.min(Math.max(0, Math.min(...ends) - now), 2_147_483_647));
  }

  async function refresh() {
    const url = container.dataset.refreshUrl;
    if (pending || stopped || !url || document.hidden) return;
    pending = true;
    try {
      const response = await fetch(url, { cache: "no-store", credentials: "same-origin" });
      if (!response.ok || stopped) return;
      const html = await response.text();
      if (stopped) return;
      const open = new Set(Array.from(container.querySelectorAll<HTMLDetailsElement>("details[open]")).map((details) => details.dataset.announcement));
      const focused = document.activeElement?.closest<HTMLDetailsElement>("details[data-announcement]")?.dataset.announcement;
      // Only this origin's auto-escaped Twig fragment is inserted; no announcement HTML is accepted.
      container.innerHTML = html;
      container.querySelectorAll<HTMLDetailsElement>("details[data-announcement]").forEach((details) => {
        details.open = open.has(details.dataset.announcement);
        if (details.dataset.announcement === focused) details.querySelector("summary")?.focus();
      });
      expire();
    } catch {
      // Keep the last successful answer through an outage; its local deadline still applies.
    } finally {
      pending = false;
    }
  }

  function onOpen(event: Event) {
    if (event.target instanceof Element && event.target.closest("summary")?.parentElement instanceof HTMLDetailsElement) void refresh();
  }
  container.addEventListener("click", onOpen);
  window.addEventListener("focus", refresh);
  document.addEventListener("visibilitychange", refresh);
  const poll = setInterval(refresh, 30_000);
  expire();
  return () => {
    stopped = true;
    clearInterval(poll);
    clearTimeout(expiry);
    container.removeEventListener("click", onOpen);
    window.removeEventListener("focus", refresh);
    document.removeEventListener("visibilitychange", refresh);
  };
}

const container = document.getElementById("service-announcements");
if (container) initializeServiceStatus(container);
